import colors from '@honeybook/hbui/dist/styles/colors';
import { Alignment } from '@honeybook/hbui/dist/tokens/types';
import type { ContainerableBlock } from '~/models/blocks/containerable-block.model';
import {
    IContainerBlockStyles,
    IImageBlockStyles,
    IVideoBlockStyles,
    ISpacerBlockStyles,
    IContractBlockStyles,
    IInvoiceBlockStyles,
    IInvoiceHeaderBlockStyles,
    IInvoiceServiceItemBlockStyles,
    IServiceSelectionItemBlockStyles,
    IInvoiceColumnHeaderBlockStyles,
    ISocialBlockStyles,
    IServiceSelectionBlockStyles,
    IServicesItemsContainerBlockStyles,
    IScheduleSessionBlockStyles,
    LineStyleTypes,
    PaddingSize,
    ContentWidth,
    InvoiceAppearanceTypes,
    IconSize,
    IconColor,
    IStyles
} from '~/types/block-styles.types';

const CONTAINER_BLOCK_INITIAL_STYLES = {
    padding_y: PaddingSize.MEDIUM,
    custom_padding_y: 40,
    content_width: ContentWidth.MEDIUM,
    background_color: colors.white,
    background_color_opacity: 0,
    grid_gap: 20,
    opacity: 100
};

export type GetInitialBlockStyles<T = IStyles> = (
    parent: ContainerableBlock
) => T;

export const INITIAL_BLOCK_STYLES: {
    container_block: GetInitialBlockStyles<IContainerBlockStyles>;
    company_info_block: GetInitialBlockStyles<IContainerBlockStyles>;
    image_block: GetInitialBlockStyles<IImageBlockStyles>;
    video_block: GetInitialBlockStyles<IVideoBlockStyles>;
    spacer_block: GetInitialBlockStyles<ISpacerBlockStyles>;
    contract_block: GetInitialBlockStyles<IContractBlockStyles>;
    invoice_container_block: GetInitialBlockStyles<IInvoiceBlockStyles>;
    invoice_header_block: GetInitialBlockStyles<IInvoiceHeaderBlockStyles>;
    invoice_service_item_block: GetInitialBlockStyles<IInvoiceServiceItemBlockStyles>;
    service_selection_item_block: GetInitialBlockStyles<IServiceSelectionItemBlockStyles>;
    invoice_column_header_block: GetInitialBlockStyles<IInvoiceColumnHeaderBlockStyles>;
    social_block: GetInitialBlockStyles<ISocialBlockStyles>;
    charge_container_block: GetInitialBlockStyles<IContainerBlockStyles>;
    service_selection_block: GetInitialBlockStyles<IServiceSelectionBlockStyles>;
    service_items_container_block: GetInitialBlockStyles<IServicesItemsContainerBlockStyles>;
    schedule_container_block: GetInitialBlockStyles<IContainerBlockStyles>;
    schedule_session_block: GetInitialBlockStyles<IScheduleSessionBlockStyles>;
} = {
    container_block: () => CONTAINER_BLOCK_INITIAL_STYLES,
    company_info_block: () => CONTAINER_BLOCK_INITIAL_STYLES,
    image_block: () => ({
        alignment: Alignment.CENTER,
        border_radius: 0,
        opacity: 100
    }),
    video_block: () => ({
        alignment: Alignment.CENTER,
        video_link: '',
        video_controls: false,
        loop_forever: false,
        auto_play: false,
        is_transparent_background: true
    }),
    spacer_block: () => ({
        line_style: LineStyleTypes.Solid,
        background_color: colors.transparent,
        show_line: false,
        height: 30,
        line_color: colors.black,
        line_alignment: Alignment.CENTER,
        line_radius: 0,
        line_thickness: 30,
        line_width: 30,
        is_hidden: false
    }),
    contract_block: () => ({
        padding_y: PaddingSize.MEDIUM,
        content_width: ContentWidth.MEDIUM,
        background_color: colors.white,
        background_color_opacity: 0
    }),
    invoice_container_block: () => ({
        padding_y: PaddingSize.MEDIUM,
        content_width: ContentWidth.MEDIUM,
        background_color: colors.white,
        background_color_opacity: 0
    }),
    invoice_header_block: () => ({
        show_company_logo: true,
        show_company_details: true,
        show_project_details: false,
        show_title: true,
        show_bill_to: true,
        show_po: true
    }),
    invoice_column_header_block: () => ({
        invoice_appearance: InvoiceAppearanceTypes.Plain,
        show_image: false,
        show_tax_1: true,
        show_tax_2: false,
        show_tax_3: false,
        show_quantity_and_price: true,
        show_unit: true
    }),
    invoice_service_item_block: parent => ({
        show_image:
            parent.flow?.invoiceColumnHeaderBlock?.styles.show_image ?? false,
        background_color: colors.white,
        background_color_opacity: 0
    }),
    service_selection_item_block: parent => ({
        show_image:
            parent.flow?.invoiceColumnHeaderBlock?.styles.show_image ?? false,
        show_description: true,
        show_quantity: true,
        show_unit_price: true,
        show_sub_item_price: true
    }),
    social_block: () => ({
        icon_alignment: Alignment.LEFT,
        icon_size: IconSize.MEDIUM,
        icon_color: IconColor.ORIGINAL
    }),
    charge_container_block: () => ({
        padding_y: PaddingSize.MEDIUM,
        content_width: ContentWidth.MEDIUM
    }),
    service_selection_block: () => ({
        padding_y: PaddingSize.MEDIUM,
        content_width: ContentWidth.MEDIUM,
        background_color: colors.transparent,
        display_image: true,
        display_description: true,
        display_quantity: true,
        display_unit: true,
        display_unit_price: true,
        display_service_price: true,
        item_background_color: 'transparent',
        background_color_opacity: 100,
        corner_radius: 8,
        shadow: 8,
        grid_gap: 20,
        service_pricing: {
            font_weight: 400,
            italic: false,
            underline: false,
            alignment: Alignment.RIGHT
        },
        service_pricing_link: {
            font_weight: 400,
            font_size: 'sm',
            italic: false,
            underline: true,
            alignment: Alignment.RIGHT
        },
        service_select_button: {
            button_alignment: Alignment.RIGHT
        },
        title: {
            font_weight: 700,
            italic: false,
            underline: false,
            alignment: Alignment.LEFT
        },
        total_price: {
            font_weight: 700,
            italic: false,
            underline: false,
            alignment: Alignment.RIGHT
        }
    }),
    service_items_container_block: () => ({
        content_width: ContentWidth.MEDIUM
    }),
    schedule_container_block: () => ({
        padding_y: PaddingSize.LARGE,
        content_width: ContentWidth.MEDIUM,
        background_color: colors.white,
        background_color_opacity: 0
    }),
    schedule_session_block: () => ({
        corner_radius: 0,
        shadow: 10,
        background_color: colors.white,
        background_color_opacity: 0
    })
};
